import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { KpiTile } from '../components/KpiTile.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Support-team dashboard home. Rendered in place of the finance-heavy
 * {@code Overview} when the signed-in admin is SUPPORT_LEAD or
 * SUPPORT_AGENT. Hides MRR / conversion / admin-team activity — none
 * of which support staff need or are authorised to act on — and
 * surfaces the queue + team velocity instead.
 *
 *   KPIs        — open / pending / new-today / replies-today
 *   Chart       — daily tickets opened vs staff replies, 30 days
 *   Activity    — recent SUPPORT actions only (no admin-team events)
 *
 * Three parallel fetches on mount. Each wrapped in its own catch so
 * one slow endpoint doesn't blank the whole page — same pattern as
 * the superadmin Overview.
 *
 * @author <lukman@zshift.io>
 */
export function SupportOverview() {
  const { me } = useAdminAuth();
  const { toast } = useUI();

  const [loading, setLoading]   = useState(true);
  const [summary, setSummary]   = useState(null);
  const [volume,  setVolume]    = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const safe = (p, fallback) => p.catch((e) => { console.warn(e); return fallback; });

    Promise.all([
      safe(api('/api/admin/stats/support-overview'),     null),
      safe(api('/api/admin/stats/support-volume'),       []),
      safe(api('/api/admin/audit/recent?supportOnly=true'), []),
    ]).then(([s, v, a]) => {
      if (cancelled) return;
      setSummary(s);
      setVolume(Array.isArray(v) ? v : []);
      setActivity(Array.isArray(a) ? a : []);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open       = summary?.openQueries    || 0;
  const pending    = summary?.pendingQueries || 0;
  const total      = summary?.totalQueries   || 0;
  const newToday   = summary?.newToday       || 0;
  const repliesToday = summary?.repliesToday || 0;
  const replies7d  = summary?.replies7d      || 0;

  // Greet by first name from the signed-in admin.
  const firstName = me?.name ? me.name.split(/\s+/)[0] : null;
  const greeting = firstName
    ? <>Hi <em>{firstName}</em></>
    : <>Support overview</>;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting}</h1>
          <p className="page-sub">
            Your support queue at a glance. Refresh the page to re-fetch — counts also
            update live in the sidebar badge every 30 seconds.
          </p>
        </div>
      </div>

      {/* ── KPI row ─────────────────────────────────────────────── */}
      <div className="kpi-row">
        <KpiTile
          label="Open queries"
          iconName="queries"
          iconColor="orange"
          value={open}
          valueFormatter={(v) => String(v)}
          sub={`/ ${total.toLocaleString()} all-time`}
          delta={{ post: 'Awaiting a staff reply' }}
          loading={loading}
        />
        <KpiTile
          label="Pending user"
          iconName="queries"
          iconColor="blue"
          value={pending}
          valueFormatter={(v) => String(v)}
          delta={{ post: 'You replied, waiting on them' }}
          loading={loading}
        />
        <KpiTile
          label="New today"
          iconName="arrowDown"
          value={newToday}
          valueFormatter={(v) => String(v)}
          delta={{ post: 'Tickets opened since midnight UTC' }}
          loading={loading}
        />
        <KpiTile
          label="Your replies today"
          iconName="send"
          iconColor="green"
          value={repliesToday}
          valueFormatter={(v) => String(v)}
          delta={{
            raw: `${replies7d.toLocaleString()} this week`,
            post: '· team total',
          }}
          loading={loading}
        />
      </div>

      {/* ── Volume chart + jump-in card ────────────────────────── */}
      <div className="row r-2-1">
        <SupportVolumeChart data={volume} loading={loading} />
        <JumpInCard openCount={open} loading={loading} />
      </div>

      {/* ── Support team activity feed ─────────────────────────── */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Recent support actions</h3>
            <div className="sub">From your team · last 20</div>
          </div>
          <div className="activity">
            {loading && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
            )}
            {!loading && activity.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                No recent support activity. The first reply / close / ban / refund will land here.
              </div>
            )}
            {!loading && activity.map((a) => {
              const h = humanizeSupportAction(a);
              return (
                <div className="act-item" key={a.id}>
                  <div className={`act-ic ${h.color}`}>
                    <Icon name={h.icon} size={14} />
                  </div>
                  <div className="act-body">
                    <strong>{h.actor}</strong>{' '}
                    <span style={{ color: 'var(--ink-2)' }}>{h.verb}</span>
                    {h.object && (
                      <>
                        {' '}<span className="mono" style={{ color: 'var(--ink-3)' }}>{h.object}</span>
                      </>
                    )}
                    <span className="act-time">{a.createdAt ? agoString(a.createdAt) : '—'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Support volume chart (hand-rolled SVG, same idiom as SignupsChart) ─
//
// Two daily series, last 30 days:
//   tickets (purple) — incoming demand
//   replies (green)  — team supply
//
// Rendered as filled-area curves on the same Y scale so "are we
// keeping up" reads at a glance: replies above tickets = team is
// draining the queue; tickets above replies = backlog growing.
const W = 800, H = 260, PAD_L = 40, PAD_R = 12, PAD_T = 12, PAD_B = 28;
const COLOR_TICKETS = '#6B3FD4'; // brand purple
const COLOR_REPLIES = '#10A37F'; // brand green

function SupportVolumeChart({ data, loading }) {
  const series = useMemo(() => (
    Array.isArray(data) && data.length > 0
      ? data
      : Array.from({ length: 30 }, () => ({ date: '', tickets: 0, replies: 0 }))
  ), [data]);

  const maxVal = useMemo(() => {
    const peak = series.reduce(
      (m, r) => Math.max(m, r.tickets || 0, r.replies || 0),
      0
    );
    if (peak === 0) return 4;          // empty / loading — show a flat baseline
    if (peak <= 2)  return Math.max(2, peak);
    if (peak <= 5)  return 5;
    if (peak <= 10) return 10;
    return Math.ceil(peak / 5) * 5;
  }, [series]);

  const yTicks = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => Math.round((maxVal * i) / steps));
  }, [maxVal]);

  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const xOf = (i) => PAD_L + (series.length === 1 ? plotW / 2 : (i * plotW) / (series.length - 1));
  const yOf = (v) => PAD_T + plotH - (v / maxVal) * plotH;

  const linePath = (key) => series.map((row, i) => {
    const cmd = i === 0 ? 'M' : 'L';
    return `${cmd}${xOf(i).toFixed(1)},${yOf(row[key] || 0).toFixed(1)}`;
  }).join(' ');

  const areaPath = (key) => {
    const top = linePath(key);
    const lastX = xOf(series.length - 1).toFixed(1);
    const baselineY = yOf(0).toFixed(1);
    const firstX = xOf(0).toFixed(1);
    return `${top} L${lastX},${baselineY} L${firstX},${baselineY} Z`;
  };

  const xTickIdx = [0, 7, 14, 21, 29].filter((i) => i < series.length);
  const formatDate = (iso) => (iso && iso.length >= 10 ? iso.slice(5) : iso || '');

  const totalTickets = series.reduce((s, r) => s + (r.tickets || 0), 0);
  const totalReplies = series.reduce((s, r) => s + (r.replies || 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Support volume</h3>
          <div className="sub">DAILY · LAST 30 DAYS</div>
        </div>
        <div className="chart-legend">
          <span className="lg">
            <span className="sw" style={{ background: COLOR_TICKETS }}/>
            New tickets
            <span style={{ marginLeft: 4, color: 'var(--ink-3)', fontFamily: "'Geist Mono', monospace", fontSize: 11 }}>
              {loading ? '—' : totalTickets.toLocaleString()}
            </span>
          </span>
          <span className="lg">
            <span className="sw" style={{ background: COLOR_REPLIES }}/>
            Staff replies
            <span style={{ marginLeft: 4, color: 'var(--ink-3)', fontFamily: "'Geist Mono', monospace", fontSize: 11 }}>
              {loading ? '—' : totalReplies.toLocaleString()}
            </span>
          </span>
        </div>
      </div>
      <div className="chart-body">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          role="img"
          aria-label="Daily support tickets opened versus staff replies sent over the last 30 days"
        >
          <defs>
            <linearGradient id="sv-tickets" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor={COLOR_TICKETS} stopOpacity="0.28"/>
              <stop offset="100%" stopColor={COLOR_TICKETS} stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="sv-replies" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor={COLOR_REPLIES} stopOpacity="0.28"/>
              <stop offset="100%" stopColor={COLOR_REPLIES} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {yTicks.map((v, i) => {
            const y = yOf(v);
            return (
              <g key={`yt-${i}`}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                      stroke="var(--line)" strokeWidth="1" />
                <text x={PAD_L - 8} y={y + 3}
                      textAnchor="end"
                      fontFamily="'Geist Mono', monospace"
                      fontSize="10"
                      fill="var(--ink-3)">
                  {v}
                </text>
              </g>
            );
          })}

          {!loading && (
            <>
              <path d={areaPath('tickets')} fill="url(#sv-tickets)" />
              <path d={linePath('tickets')} fill="none" stroke={COLOR_TICKETS} strokeWidth="2" />
              <path d={areaPath('replies')} fill="url(#sv-replies)" />
              <path d={linePath('replies')} fill="none" stroke={COLOR_REPLIES} strokeWidth="2" />
            </>
          )}

          {xTickIdx.map((i) => (
            <text key={`xt-${i}`}
                  x={xOf(i)} y={H - 8}
                  textAnchor="middle"
                  fontFamily="'Geist Mono', monospace"
                  fontSize="10"
                  fill="var(--ink-3)">
              {formatDate(series[i].date)}
            </text>
          ))}

          {loading && (
            <text x={W / 2} y={H / 2}
                  textAnchor="middle"
                  fontFamily="Geist, sans-serif"
                  fontSize="13"
                  fill="var(--ink-3)">
              Loading…
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}

// ── Sidebar quick-jump card (right column under the chart) ────────
function JumpInCard({ openCount, loading }) {
  return (
    <div className="card" style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h3 style={{ margin: 0 }}>Get to work</h3>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        {loading
          ? 'Checking the queue…'
          : openCount > 0
            ? <>You have <strong>{openCount.toLocaleString()}</strong> open ticket{openCount === 1 ? '' : 's'} waiting on a reply.</>
            : 'Queue is clear — nice work.'}
      </p>
      <a
        href="/queries"
        className="btn primary"
        onClick={(e) => {
          // Use react-router for the navigation — match the same
          // ghost-button-style anchor used elsewhere in the codebase.
          e.preventDefault();
          window.history.pushState({}, '', '/queries');
          window.dispatchEvent(new Event('popstate'));
        }}
        style={{ alignSelf: 'flex-start' }}
      >
        Open the queue
        <Icon name="arrowRight" size={13} style={{ marginLeft: 6 }} />
      </a>
    </div>
  );
}

// ── Activity-feed copy mapping (support actions only) ─────────────
//
// Subset of the full Overview's MAP — we only ever receive the
// support actions thanks to the server's supportOnly=true filter, so
// the lookup table stays tight + the "unknown" fallback only fires
// if a future support action is added without being added here.
function humanizeSupportAction(a) {
  const code  = a.action || '';
  const target = a.target || '';
  const actor = a.actorEmail || (a.actorId == null ? 'system' : `admin#${a.actorId}`);

  const MAP = {
    reply_query:   { verb: 'replied to',                    icon: 'queries', color: 'blue'   },
    close_query:   { verb: 'closed',                        icon: 'check',   color: 'green'  },
    ban_user:      { verb: 'banned',                        icon: 'ban',     color: 'red'    },
    unban_user:    { verb: 'un-banned',                     icon: 'check',   color: 'green'  },
    change_plan:   { verb: 'changed plan of',               icon: 'plans',   color: 'blue'   },
    refund_intent: { verb: 'initiated a refund for',        icon: 'finances',color: 'orange' },
  };

  const hit = MAP[code];
  if (hit) return { actor, verb: hit.verb, object: target, icon: hit.icon, color: hit.color };
  return {
    actor,
    verb: code.replace(/_/g, ' ') || 'did something',
    object: target,
    icon: 'logs',
    color: '',
  };
}

function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1)  return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24)    return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30)    return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}
